import { useEffect } from 'react';
import reset from '~/assets/icons/reset.svg';
import { playSound, Sound } from '~/assets/sounds';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { hexToRgb, rgbToHex } from '~/shared';
import {
	setSelectedColor,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import {
	StyledHexTextInput,
	StyledInputContainer,
	StyledRgbContainer,
	StyledRgbTextInput,
} from './ColorPickerInputs.styled';

const ColorPickerInputs = () => {
	const selectedColor = useLayoutSelector(
		(state) => state.colorPicker.selectedColor,
	);
	const dispatch = useLayoutDispatch();

	useEffect(() => {
		const inputR = document.getElementById('rgbR') as HTMLInputElement | null;
		const inputG = document.getElementById('rgbG') as HTMLInputElement | null;
		const inputB = document.getElementById('rgbB') as HTMLInputElement | null;
		const inputHex = document.getElementById('hex') as HTMLInputElement | null;
		if (
			inputR === null ||
			inputG === null ||
			inputB === null ||
			inputHex === null
		) {
			return;
		}

		const activeId = document.activeElement?.id || null;
		if (activeId !== 'rgbR')
			inputR.value = selectedColor?.r?.toString() || '255';
		if (activeId !== 'rgbG')
			inputG.value = selectedColor?.g?.toString() || '255';
		if (activeId !== 'rgbB')
			inputB.value = selectedColor?.b?.toString() || '255';

		const hexColor = rgbToHex(selectedColor || { r: 255, g: 255, b: 255 });
		if (activeId !== 'hex') inputHex.value = hexColor;
	}, [selectedColor]);

	const handleHexInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const textInput = event.target.value.replace(/[^a-fA-F0-9]/g, '');
		event.target.value = textInput;

		const rgbColor = hexToRgb(textInput);
		dispatch(setSelectedColor(rgbColor));
	};

	const handleRgbInputChange = (
		event: React.ChangeEvent<HTMLInputElement>,
		c: string,
	) => {
		const rgbColor = { ...selectedColor, [c]: event.target.value };
		dispatch(setSelectedColor(rgbColor));
	};

	const handleReset = () => {
		dispatch(setSelectedColor({ r: 255, g: 255, b: 255 }));
	};

	return (
		<StyledInputContainer>
			<StyledHexTextInput
				id="hex"
				hint="#"
				onChange={handleHexInputChange}
				onMouseEnter={() => playSound(Sound.LinkCursor)}
			/>
			<StyledRgbContainer>
				<StyledRgbTextInput
					id="rgbR"
					hint="R"
					defaultValue={selectedColor?.r || 255}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						handleRgbInputChange(e, 'r')
					}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
				/>
				<StyledRgbTextInput
					id="rgbG"
					hint="G"
					defaultValue={selectedColor?.g || 255}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						handleRgbInputChange(e, 'g')
					}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
				/>
				<StyledRgbTextInput
					id="rgbB"
					hint="B"
					defaultValue={selectedColor?.b || 255}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						handleRgbInputChange(e, 'b')
					}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
				/>
			</StyledRgbContainer>
			<Button
				color={ButtonColors.Default}
				height={ButtonHeight.Height40}
				square
				onClick={handleReset}
			>
				<img src={reset} width={20} height={20} />
			</Button>
		</StyledInputContainer>
	);
};

export { ColorPickerInputs };
